import React, { memo } from 'react'
import { Platform } from 'react-native'

import Avatar from 'app/components/base/Avatar'
import Rating from 'app/components/base/Rating'
import Skeleton from 'app/components/base/Skeleton'
import Stack, { StackProps } from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import LinkUserStore from 'app/components/common/LinkUserStore'
import ReplyLink from 'app/features/Store/components/ReplyLink'

type ReviewsCardProps = {
  reviewId: string
  avatarUrl?: string
  fullName: string
  reviewDate: string
  comment: string
  rate?: number
  receiverAvatarUrl?: string
  receiverFullName?: string
  replyDate?: string
  replyComment?: string
  showAction: boolean
  isReplyCard?: boolean
  loading?: boolean
  slug?: string
  sx?: StackProps['sx']
}

const ReviewsCard: RFC<ReviewsCardProps> = ({
  reviewId,
  avatarUrl,
  fullName,
  reviewDate,
  comment,
  rate,
  receiverAvatarUrl,
  receiverFullName,
  replyDate,
  replyComment,
  showAction,
  isReplyCard,
  loading = false,
  slug,
  sx
}) => {
  const ReplyCard = (
    <>
      {!!replyComment && (
        <ReviewsCard
          avatarUrl={receiverAvatarUrl}
          comment={replyComment || ''}
          fullName={receiverFullName || ''}
          reviewDate={replyDate || ''}
          reviewId={reviewId}
          sx={{ pt: 2, pl: 7 }}
          showAction={showAction}
          loading={loading}
          isReplyCard
        />
      )}
    </>
  )

  const showReplyLink = !isReplyCard && !replyComment

  return (
    <Stack spacing={1} sx={sx}>
      <Stack spacing={2} direction="row" alignItems="center">
        <Avatar
          loading={loading}
          src={avatarUrl}
          sizes="85px"
          width={40}
          height={40}
          {...Platform.select({ native: { layout: 'fill' } })}
          sx={{ width: 40, height: 40 }}
        />

        <Stack
          spacing={0.5}
          direction="column"
          justifyContent="space-between"
          sx={{ ...Platform.select({ native: { flex: 1 } }) }}
        >
          <Skeleton loading={loading} width={200}>
            <LinkUserStore
              slug={slug}
              variant="body2"
              lineClamp={2}
              sx={{
                fontWeight: 500,
                color: 'text.light'
              }}
            >
              {fullName}
            </LinkUserStore>
          </Skeleton>

          <Skeleton loading={loading} width={200}>
            <Text variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {reviewDate}
            </Text>
          </Skeleton>
        </Stack>
      </Stack>

      <Skeleton loading={loading} width="100%">
        <Stack spacing={1}>
          {!!rate && (
            <Rating
              value={rate}
              size="small"
              readOnly
              {...Platform.select({
                native: {
                  sx: { justifyContent: 'flex-start' },
                  sxStar: { mx: 0 }
                }
              })}
            />
          )}

          {!!comment && (
            <Text
              variant="subtitle1"
              sx={{
                color: 'text.light',
                wordBreak: 'break-word'
              }}
            >
              {comment}
            </Text>
          )}

          {showAction && (
            <>{showReplyLink && <ReplyLink reviewId={reviewId} />}</>
          )}
        </Stack>
      </Skeleton>

      {ReplyCard}
    </Stack>
  )
}

export default memo(ReviewsCard)
